import React, { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { FileUpload } from 'primereact/fileupload';
import { Calendar } from 'primereact/calendar';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';

const typeOptions = [
    { label: 'Syllabus', value: 'Syllabus' },
    { label: 'Resources', value: 'Resources' },
    { label: 'Question Paper', value: 'QuestionPaper' },
    { label: 'Assignments', value: 'Assignments' },
];

const AddEditLMS = ({ onCancel, masterData, schoolId, financialYear, userId }) => {
    const { catchResponse } = useCatchResponse();
    const fileUploadRef = useRef(null);
    const isOrgOwner = String(userId) === String(schoolId);

    const [name, setName] = useState('');
    const [nameErr, setNameErr] = useState(false);
    const [notify, setNotify] = useState(true);
    const [type, setType] = useState('Syllabus');
    const [academicYear, setAcademicYear] = useState([]);
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [semester, setSemester] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [remarks, setRemarks] = useState('');
    const [dueDate, setDueDate] = useState(null);
    const [maxMarks, setMaxMarks] = useState('');

    const [yearList, setYearList] = useState([]);
    const [classList, setClassList] = useState([]);
    const [termList, setTermList] = useState([]);
    const [subjectList, setSubjectList] = useState([]);

    const [attachments, setAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    useEffect(() => {
        loadDropdowns();
        if (masterData?._id) {
            setName(masterData.Name || '');
            setNotify(masterData.Notify !== false);
            setType(masterData.Type || 'Syllabus');
            setAcademicYear(masterData.AcademicYear || []);
            setSelectedClasses(masterData.Class || []);
            setSemester(masterData.Term || []);
            setSubjects(masterData.Subject || []);
            setRemarks(masterData.Remarks || '');
            setDueDate(masterData.DueDate ? new Date(masterData.DueDate) : null);
            setMaxMarks(masterData.MaxMarks || '');
            setExistingAttachments(masterData.Attachments || []);
        }
    }, []);

    const loadDropdowns = async () => {
        try {
            const years = await CommonConfig.getFinancialYears(schoolId);
            setYearList((years || []).map(y => ({ value: y._id, label: y.label })));
        } catch (err) { console.log(err); }

        try {
            const classRes = await api.post(APIConstant.path.getClassList, { UserID: userId, SchoolId: schoolId, FinancialYearId: financialYear, ScopeBy: 'Teaching' });
            if (classRes.success) {
                setClassList((classRes.data || []).filter(x => x?.Status === 'Active').map(x => ({
                    value: x?._id,
                    label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}`,
                })));
            }
        } catch (err) { console.log(err); }

        try {
            const termRes = await api.post(APIConstant.path.getTermList, { UserID: schoolId });
            if (termRes.success) {
                setTermList((termRes.data || []).map(x => ({
                    value: x?._id,
                    label: x?.label || x?.TermName,
                })));
            }
        } catch (err) { console.log(err); }

        try {
            const subjectRes = await api.post(APIConstant.path.getSubjectList, { UserID: schoolId });
            if (subjectRes.success) {
                setSubjectList((subjectRes.data || []).map(x => ({
                    value: x?._id,
                    label: x?.SubjectName,
                    Classes: x?.Classes || '',
                    Teachers: x?.Teachers || '',
                    IsGrade: x?.IsGrade === true,
                })));
            }
        } catch (err) { console.log(err); }
    };

    const validate = () => {
        let valid = true;
        if (CommonConfig.isEmpty(name)) { setNameErr(true); valid = false; } else { setNameErr(false); }
        return valid;
    };

    const handleSave = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const formData = new FormData();
            if (masterData?._id) formData.append('id', masterData._id);
            formData.append('Name', name.trim());
            formData.append('Notify', notify);
            formData.append('Type', type);
            formData.append('AcademicYear', JSON.stringify(academicYear || []));
            formData.append('Class', JSON.stringify(selectedClasses || []));
            formData.append('Term', JSON.stringify(semester || []));
            formData.append('Subject', JSON.stringify(subjects || []));
            formData.append('Remarks', remarks || '');
            if (type === 'Assignments') {
                formData.append('DueDate', dueDate ? new Date(dueDate).toISOString() : '');
                formData.append('MaxMarks', isGradeSubject ? '' : (maxMarks || ''));
            }
            formData.append('SchoolId', schoolId);
            formData.append('CreatedBy', userId);

            if (masterData?._id) {
                formData.append('ExistingAttachments', JSON.stringify(existingAttachments || []));
            }
            attachments.forEach(file => formData.append('Attachments', file));

            const response = await api.post(APIConstant.path.AddLMS, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (response.success) {
                Toast.success({ message: response.message });
                Loader.hide();
                onCancel();
            } else {
                Toast.error({ message: response.message });
                Loader.hide();
            }
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleFileSelect = (e) => {
        const picked = e.files || [];
        setAttachments(prev => {
            const merged = [...prev];
            picked.forEach(f => {
                if (!merged.some(m => m.name === f.name && m.size === f.size)) merged.push(f);
            });
            return merged;
        });
        if (fileUploadRef.current) fileUploadRef.current.clear();
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingAttachment = (index) => {
        setExistingAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const isImage = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');

    const filteredSubjectList = React.useMemo(() => {
        let listData = subjectList;
        if (!isOrgOwner) {
            listData = listData.filter(s =>
                (subjects || []).includes(s.value) ||
                (s.Teachers || '').split(',').map(t => t.trim()).includes(String(userId))
            );
        }
        if (!selectedClasses?.length) return listData;
        return listData.filter(s =>
            (subjects || []).includes(s.value) ||
            (s.Classes || '').split(',').map(c => c.trim()).some(c => selectedClasses.includes(c))
        );
    }, [subjectList, selectedClasses, subjects, isOrgOwner, userId]);

    const isGradeSubject = React.useMemo(
        () => (subjects || []).some(id => subjectList.find(s => s.value === id)?.IsGrade === true),
        [subjects, subjectList]);

    return (
        <>
            <style>{`
                .lms-file-upload-wrapper .p-button.p-fileupload-choose {
                    height: 35px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: none;
                    border: none;
                    outline: none;
                    padding: 0rem 1rem;
                    color: #ffffff;
                    font-size: 14px;
                    font-weight: 500 !important;
                    white-space: nowrap;
                    border-radius: 0.35rem;
                    background: var(--button-bg, #14452f);
                    width: fit-content;
                }
            `}</style>
            <div className="p-d-flex p-flex-column p-jc-between p-mx-3 p-h-100">
                <div className="p-grid">
                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Name *</label>
                            <InputText className="p-w-100 p-mt-1" value={name} maxLength={150} onChange={(e) => setName(e.target.value)} />
                        </div>
                        {nameErr && <span style={{ color: 'red', fontSize: '12px' }}>Name is required</span>}
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2 p-d-flex p-ai-center">
                        <Checkbox inputId="notify" checked={notify} onChange={(e) => setNotify(e.checked)} />
                        <label htmlFor="notify" className="p-ml-2">Notify</label>
                    </div>

                    <div className="p-col-12 p-pt-2">
                        <div className="custom-inputs">
                            <label>Type</label>
                            <div className="p-d-flex p-flex-wrap p-mt-1" style={{ gap: '20px' }}>
                                {typeOptions.map(opt => (
                                    <div key={opt.value} className="p-d-flex p-ai-center">
                                        <RadioButton inputId={`type-${opt.value}`} name="lmsType" value={opt.value}
                                            onChange={(e) => setType(e.value)} checked={type === opt.value} />
                                        <label htmlFor={`type-${opt.value}`} className="p-ml-2">{opt.label}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Academic Year</label>
                            <MultiSelect className="p-w-100 p-mt-1" value={academicYear} options={yearList} onChange={(e) => setAcademicYear(e.value)} placeholder="Select Academic Year" filter display="chip" />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Class/Course</label>
                            <MultiSelect className="p-w-100 p-mt-1" value={selectedClasses} options={classList} onChange={(e) => setSelectedClasses(e.value)} placeholder="Select Class/Course" filter display="chip" />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Semester</label>
                            <MultiSelect className="p-w-100 p-mt-1" value={semester} options={termList} onChange={(e) => setSemester(e.value)} placeholder="Select Semester" filter display="chip" />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Subject</label>
                            <MultiSelect className="p-w-100 p-mt-1" value={subjects} options={filteredSubjectList} onChange={(e) => setSubjects(e.value)} placeholder="Select Subject" filter display="chip" />
                        </div>
                    </div>

                    {type === 'Assignments' && (
                        <>
                            <div className="p-col-12 p-md-6 p-pt-2">
                                <div className="custom-inputs">
                                    <label>Submission Deadline</label>
                                    <Calendar className="p-w-100 p-mt-1" value={dueDate} onChange={(e) => setDueDate(e.value)} showTime hourFormat="12" dateFormat="dd/mm/yy" placeholder="Select deadline" showButtonBar />
                                </div>
                            </div>
                            <div className="p-col-12 p-md-6 p-pt-2">
                                <div className="custom-inputs">
                                    <label>{isGradeSubject ? 'Evaluation' : 'Max Marks'}</label>
                                    {isGradeSubject ? (
                                        <div className="p-mt-1" style={{ fontSize: 13, color: '#475569', padding: '9px 0' }}>
                                            <i className="pi pi-info-circle" style={{ marginRight: 6, color: '#2f6fed' }} />
                                            This is a graded subject — students will receive a <b>Grade</b> instead of marks.
                                        </div>
                                    ) : (
                                        <InputText className="p-w-100 p-mt-1" value={maxMarks} keyfilter="pint" maxLength={4} onChange={(e) => setMaxMarks(e.target.value)} placeholder="e.g. 20" />
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="p-col-12 p-pt-2">
                        <div className="custom-inputs">
                            <label>Remarks</label>
                            <InputTextarea className="p-w-100 p-mt-1" value={remarks} rows={3} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="p-col-12 p-pt-3">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>Attachment</label>
                        {existingAttachments.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                {existingAttachments.map((file, i) => {
                                    const url = `${apiBase}/Upload/LMS/${file}?token=${CommonConfig.fileToken()}`;
                                    const label = file.replace(/^\d+-/, '');
                                    return (
                                        <div key={i} style={{ position: 'relative', width: '90px' }}>
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                {isImage(file)
                                                    ? <img src={url} alt={label} style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                                                    : <div style={{ width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#f6f7f9' }}><i className="pi pi-file" style={{ fontSize: '30px', color: '#6c757d' }}></i></div>}
                                            </a>
                                            <Button icon="pi pi-times" className="p-button-rounded p-button-sm p-button-danger" style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px' }} onClick={() => removeExistingAttachment(i)} />
                                            <div style={{ fontSize: '11px', marginTop: '4px', wordBreak: 'break-all', textAlign: 'center' }}>{label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="lms-file-upload-wrapper">
                            <FileUpload ref={fileUploadRef} name="Attachments" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                                customUpload auto uploadHandler={handleFileSelect} mode="basic" chooseLabel="Choose Files" maxFileSize={10000000} />
                        </div>
                        {attachments.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                                {attachments.map((file, i) => (
                                    <div key={i} style={{ position: 'relative', width: '90px' }}>
                                        {isImage(file.name)
                                            ? <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                                            : <div style={{ width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#f6f7f9' }}><i className="pi pi-file" style={{ fontSize: '30px', color: '#6c757d' }}></i></div>}
                                        <Button icon="pi pi-times" className="p-button-rounded p-button-sm p-button-danger" style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px' }} onClick={() => removeAttachment(i)} />
                                        <div style={{ fontSize: '11px', marginTop: '4px', wordBreak: 'break-all', textAlign: 'center' }}>{file.name}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
                    <Button className="p-m-0 btn-text-action-secondary p-m-1" label="Cancel" onClick={onCancel} />
                    <Button className="p-m-0 btn-text-action-primary submit-focus" label="Save" onClick={handleSave} />
                </div>
            </div>
        </>
    );
};

export default AddEditLMS;
